import { citationWords } from "@workforceos/shared";
import { useParams, Link } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { formatWhen } from "../../lib/when";

/** The agents whose runs produce a deck. Mirrors the API's list. */
const PACK_HANDS = new Set(["h01_sales_analyst", "h02_weekly_pack", "h05_board_pack"]);
import { roleLabel } from "../../lib/roleLabel";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../../api/client";
import { verdictLabel, verdictTone } from "./Workforce";
import { BarChart, type BarDatum } from "../../components/charts/BarChart";
import { validatorLabel } from "../../lib/validatorLabel";

/**
 * One run, in full (NEW-WF-3).
 *
 * This screen is the argument. It shows, in order: what the deterministic
 * side computed, which rules were consulted and what each said with its
 * citation, whether a model was called at all, and the narrative as it reads
 * after every figure was substituted.
 *
 * Two distinctions it must never blur, because both are the difference
 * between a claim and a fact:
 *   - a rule PERMITTED to block is not a rule that DID block;
 *   - a placeholder threshold is not a statutory one, and the run detail says
 *     which it was looking at.
 */

interface RuleEvaluationView {
  rule_id: string;
  pack_key: string;
  pack_version: string;
  as_of: string;
  state: string;
  verdict: string;
  declared_verdict: string | null;
  verdict_capped_by_origin: boolean;
  can_block: boolean;
  origin: string;
  origin_ref: string | null;
  citation: string;
  human_summary: string;
  source: string;
}

interface RunDetailResponse {
  run: {
    run_id: string;
    hand_key: string;
    hand_name: string;
    verdict: string;
    duration_ms: number;
    created_at: string;
  };
  gate_pre: {
    calculated_fields?: Record<string, unknown>;
    verdict?: string;
    missing?: string[];
    unknown_facts?: string[];
    /** The Hand's stated reasons for rules its pre-gate could not assess. */
    assessed_by_post_gate?: Record<string, string>;
    pack_version?: string;
    source?: string;
    trace_id?: string;
    /** Set on a run that threw (D1): where it stopped, and the error. */
    failed_at?: string;
    error?: string;
  };
  /** Which bodies of rules this run was resolved against, and from where. */
  checked_against?: {
    pack_key: string;
    label: string;
    instrument: string;
    scope: string;
    rules: number;
    obligations: number;
    placeholders: number;
    articles: string[];
    source: string;
  }[];
  feedback?: {
    up: number;
    down: number;
    mine: "up" | "down" | null;
    notes: { verdict: string; note: string | null; who: string | null }[];
  };
  gate_post: {
    schema_ok: boolean;
    numeric_substitutions: number;
    failed_validators: string[];
    verdict: string;
    outcomes?: { name: string; ok: boolean; detail?: string }[];
    substituted?: { en?: string; ar?: string };
  } | null;
  llm: { model: string; tokens_in: number; tokens_out: number } | null;
  rules: RuleEvaluationView[];
  resolution: {
    decision: string;
    reason: string;
    resolved_role: string;
    resolved_at: string;
    blocking_rule_ids: string[];
  } | null;
}

/**
 * How a rule's authority is described. `origin` decides whether a rule may
 * ever block, and a placeholder must never be presented as law — 17 of the
 * 32 rules in this pack are unconfirmed numbers.
 */
function originLabel(origin: string, ref: string | null): string {
  switch (origin) {
    case "statutory":
      return ref ? `Statute · ${ref}` : "Statute";
    case "regulator_rule":
      return ref ? `Regulator · ${ref}` : "Regulator rule";
    case "sharia_ruling":
      return ref ? `Sharia ruling · ${ref}` : "Sharia ruling";
    case "tenant_policy":
      return ref ? `Client policy · ${ref}` : "Client policy (unattributed)";
    case "xamun_placeholder":
      return "Placeholder — not policy, not statute";
    default:
      return origin;
  }
}

/**
 * Calculated fields carry the calculator's own names, and those names are for
 * the brief's placeholders — not for a person. `est_retained_spread_aed` on a
 * screen a client reads is a debug dump, the same defect as the raw `cxo`
 * enum that reached every page during the fork.
 *
 * Anything not named here is title-cased rather than hidden: a field with an
 * ugly label is a smaller problem than a figure that silently vanishes from
 * the audit trail.
 */
const FIELD_LABELS: Record<string, string> = {
  as_of: "Reporting date",
  window_days: "Baseline window (days)",
  wow_window_start: "Week starts",
  gross_aed_total: "Gross subscriptions (AED)",
  redemptions_aed_total: "Redemptions (AED)",
  net_inflow_aed: "Net inflow (AED)",
  txn_count: "Transactions",
  avg_ticket_aed: "Average ticket (AED)",
  est_retained_spread_aed: "Est. retained spread (AED)",
  est_management_fee_aed: "Est. management fee (AED)",
  wow_gross_pct: "Week on week (%)",
  velocity_median_days: "Median lead to funding (days)",
  anomaly_count: "Anomalies found",
  anomalies: "Anomalies",
  top_movers: "Biggest movers",
  by_channel: "By channel",
  series: "Series",
  value: "Value",
  mean_window: "Trailing mean",
  hand_key: "Agent",
  filter_hand_key: "Agent it was narrowed to",
  filter_person: "Narrowed to one person",
  person_name: "Person",
  person_ambiguous: "Name matched more than one person",
  decisions_listed: "Decisions listed",
  rewrite_of: "Rewrite of run",
  original_copy: "Original copy",
  mean_28d: "Trailing mean",
  z: "Standard deviations",
  direction: "Direction",
  channel: "Channel",
  gross_aed: "Gross (AED)",
  share_pct: "Share (%)",
  delta_aed: "Change vs 7-day mean (AED)",
  // H06
  asset_id: "Asset",
  campaign_name: "Campaign",
  product_key: "Product code",
  product_name: "Product",
  copy_length_chars: "Copy length (characters)",
  forbidden_hit_count: "Forbidden claims found",
  forbidden_hits: "Forbidden claims",
  forbidden_phrases_found: "Phrases",
  negated_mention_count: "Mentions in the required disclaimer",
  negated_mentions: "Mentions in the required disclaimer",
  disclosures_present: "Disclosures present",
  disclosures_missing: "Disclosures missing",
  arabic_parity: "Arabic version",
  fatwa_ref_present: "Fatwa reference",
  minimum_claimed_aed: "Minimum claimed (AED)",
  published_minimum_aed: "Published minimum (AED)",
  terms_match: "Terms match the catalogue",
  mentions_rewards: "Mentions rewards",
  mentions_history: "Mentions past performance",
  phrase: "Phrase",
  lang: "Language",
  span: "Position in copy",
  context: "In context",
  // H03
  case_id: "Case",
  docs_complete: "Documents complete",
  docs_missing: "Documents outstanding",
  docs_missing_count: "Documents outstanding",
  name_matches: "Name matches passport",
  dob_matches: "Date of birth matches passport",
  consistency_ok: "Identity documents consistent",
  screening_hit: "Sanctions or PEP screening hit",
  pep_flag: "Politically exposed person",
  sof_present: "Source of funds declared",
  sof_declared: "Source of funds",
  initial_amount_aed: "Initial investment (AED)",
  monthly_income_aed: "Declared monthly income (AED)",
  risk_tier: "Risk tier",
};

function fieldLabel(key: string): string {
  const known = FIELD_LABELS[key];
  if (known) return known;
  const pretty = key.replace(/_/g, " ");
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
}

/**
 * A bare identifier in a list, as a person reads it.
 *
 * The disclosure names come from the pack's own params, so a lookup table
 * here would go stale the moment GraphIQ adds one. Prettifying the token is
 * honest and survives a pack change:
 * `mudarabah_profit_share_or_anticipated_profit` was on the screen verbatim.
 */
function tokenLabel(token: string): string {
  if (!/^[a-z0-9_]+$/.test(token)) return token;
  const words = token.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function isScalar(v: unknown): boolean {
  return (
    typeof v === "string" || typeof v === "number" || typeof v === "boolean"
  );
}

function formatValue(v: unknown): string {
  if (typeof v === "number") return v.toLocaleString("en-US");
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

/**
 * Lists the run computed that are worth drawing rather than tabulating.
 * Anything not recognised here still renders as rows below — a chart is a
 * nicety, and a field that has no chart must not disappear because of it.
 */
function chartsFor(fields: Record<string, unknown>): {
  channels: BarDatum[];
  anomalies: BarDatum[];
  movers: BarDatum[];
} {
  const byChannel = (fields["by_channel"] as
    | { channel: string; gross_aed: number; share_pct: number }[]
    | undefined) ?? [];
  const anomalies = (fields["anomalies"] as
    | { series: string; z: number; direction: string; value: number }[]
    | undefined) ?? [];
  const movers = (fields["top_movers"] as
    | { channel: string; delta_aed: number }[]
    | undefined) ?? [];

  return {
    channels: [...byChannel]
      .sort((a, b) => b.gross_aed - a.gross_aed)
      .slice(0, 8)
      .map((c) => ({
        label: c.channel,
        sub: `${c.share_pct}% of the day`,
        value: c.gross_aed,
      })),
    anomalies: anomalies.map((a) => ({
      label: a.series,
      sub: `${a.direction === "down" ? "below" : "above"} the 28-day mean`,
      value: a.z,
      // Colour carries the direction, never the severity: the threshold is
      // the calculator's and it is the same for both.
      tone: a.direction === "down" ? ("bad" as const) : ("warn" as const),
    })),
    movers: movers.map((m) => ({
      label: m.channel,
      sub: "vs 7-day mean",
      value: m.delta_aed,
      tone: m.delta_aed < 0 ? ("bad" as const) : ("good" as const),
    })),
  };
}

function CalculatedFields({ fields }: { fields: Record<string, unknown> }) {
  const scalars = Object.entries(fields).filter(([, v]) => isScalar(v));
  const lists = Object.entries(fields).filter(([, v]) => Array.isArray(v));
  const charts = chartsFor(fields);
  const drawn = new Set(
    [
      charts.channels.length > 0 ? "by_channel" : "",
      charts.anomalies.length > 0 ? "anomalies" : "",
      charts.movers.length > 0 ? "top_movers" : "",
    ].filter(Boolean),
  );
  return (
    <>
      <div className="play-meta" data-testid="calculated-fields">
        {scalars.map(([k, v]) => (
          <span key={k}>
            {fieldLabel(k)} <span className="v">{formatValue(v)}</span>
          </span>
        ))}
      </div>
      {charts.channels.length > 0 ? (
        <BarChart
          testId="chart-channels"
          title="GROSS BY CHANNEL"
          unit="AED"
          data={charts.channels}
        />
      ) : null}
      {charts.anomalies.length > 0 ? (
        <BarChart
          testId="chart-anomalies"
          title="ANOMALIES — STANDARD DEVIATIONS FROM THE 28-DAY MEAN"
          data={charts.anomalies}
        />
      ) : null}
      {charts.movers.length > 0 ? (
        <BarChart
          testId="chart-movers"
          title="BIGGEST MOVERS"
          unit="AED"
          data={charts.movers}
        />
      ) : null}
      {lists.map(([k, v]) => {
        const arr = v as unknown[];
        if (arr.length === 0) return null;
        // Drawn above; not repeated as rows.
        if (drawn.has(k)) return null;
        return (
          <div key={k} style={{ marginTop: 10 }}>
            <div className="section-eyebrow">
              {fieldLabel(k).toUpperCase()}
            </div>
            {arr.map((item, i) => (
              <div key={i} className="play-meta">
                {typeof item === "object" && item !== null ? (
                  Object.entries(item as Record<string, unknown>).map(
                    ([ik, iv]) => (
                      <span key={ik}>
                        {fieldLabel(ik)}{" "}
                        {/* A role is a membership key ("cxo"): a person reads its label. */}
                        <span className="v">{ik === "role" && typeof iv === "string" ? roleLabel(iv) : formatValue(iv)}</span>
                      </span>
                    ),
                  )
                ) : (
                  <span>{tokenLabel(String(item))}</span>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}


/**
 * Was this right?
 *
 * The run rows say what the system did. Only the person who read the output
 * knows whether it was any good, and a run that completed unattended and was
 * quietly wrong looked exactly like one that was right — which is the thing a
 * supervisor most needs before letting a decision run on its own. Two clicks,
 * an optional note, and it counts against that decision's promotion.
 *
 * The note is free text a person typed. Like a fork's reason it is for the
 * next person and never for a model (CH-V-4).
 */
function Verdict({
  workspaceId,
  runId,
  feedback,
}: {
  workspaceId: string;
  runId: string;
  feedback: RunDetailResponse["feedback"];
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [noting, setNoting] = useState<"up" | "down" | null>(null);
  const send = useMutation({
    mutationFn: (verdict: "up" | "down") =>
      apiPost(`/api/workspaces/${workspaceId}/hand-runs/${runId}/feedback`, {
        verdict,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      setNoting(null);
      setNote("");
      void qc.invalidateQueries({ queryKey: ["hand-run", workspaceId, runId] });
      void qc.invalidateQueries({ queryKey: ["autonomy", workspaceId] });
    },
  });
  const mine = feedback?.mine ?? null;
  return (
    <>
      <div className="section-h">
        <span className="ttl">Was this right?</span>
        <span className="meta" data-testid="feedback-counts">
          {feedback?.up ?? 0} said yes · {feedback?.down ?? 0} said no
        </span>
      </div>
      <div className="play-card" data-testid="run-feedback">
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className={`btn ${mine === "up" ? "primary" : ""}`}
            data-testid="feedback-up"
            disabled={send.isPending}
            onClick={() => (noting === "up" ? send.mutate("up") : setNoting("up"))}
          >
            {mine === "up" ? "You said it was right" : "It was right"}
          </button>
          <button
            type="button"
            className={`btn ${mine === "down" ? "primary" : ""}`}
            data-testid="feedback-down"
            disabled={send.isPending}
            onClick={() => (noting === "down" ? send.mutate("down") : setNoting("down"))}
          >
            {mine === "down" ? "You said it was wrong" : "It was wrong"}
          </button>
          {noting ? (
            <>
              <input
                aria-label="What was wrong"
                placeholder={
                  noting === "down"
                    ? "What was wrong? (optional, for the next person)"
                    : "Anything worth noting? (optional)"
                }
                value={note}
                onChange={(e) => setNote(e.target.value)} style={{ flex: "1 1 260px", minWidth: 200 }}
              />
              <button
                type="button"
                className="btn primary"
                data-testid="feedback-submit"
                disabled={send.isPending}
                onClick={() => send.mutate(noting)}
              >
                {send.isPending ? "Recording…" : "Record"}
              </button>
            </>
          ) : null}
        </div>
        <div className="play-meta" style={{ marginTop: 8 }}>
          <span>
            This counts against the agent's promotion: a decision nobody has
            marked wrong earns autonomy, and one that has been does not.
          </span>
        </div>
        {(feedback?.notes ?? []).length > 0 ? (
          <div style={{ marginTop: 8 }} data-testid="feedback-notes">
            {feedback!.notes.map((n, i) => (
              <div key={i} className="play-meta">
                <span>
                  {n.verdict === "up" ? "Right" : "Wrong"}
                  {n.who ? ` · ${n.who}` : ""}: “{n.note}”
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}

function RuleRow({
  rule,
  assessedLaterReason,
}: {
  rule: RuleEvaluationView;
  assessedLaterReason?: string | undefined;
}) {
  return (
    <div className="play-card" data-testid={`rule-${rule.rule_id}`}>
      <div className="play-head">
        <div>
          <div className="play-arch">{rule.rule_id}</div>
          <div className="play-title">{rule.human_summary}</div>
        </div>
        <div className="play-status">
          <span
            className={`pill ${
              rule.verdict === "block"
                ? "red"
                : rule.verdict === "warn"
                  ? "amber"
                  : "green"
            }`}
          >
            {rule.state === "unknown" ? "not assessed" : rule.verdict}
          </span>
        </div>
      </div>
      <div className="play-meta">
        <span>{originLabel(rule.origin, rule.origin_ref)}</span>
        {/* Permission, not outcome. A rule may be entitled to block and pass. */}
        <span>
          May block <span className="v">{rule.can_block ? "yes" : "no"}</span>
        </span>
        {rule.verdict_capped_by_origin ? (
          <span>
            <span className="v">
              asked to {rule.declared_verdict}, capped to {rule.verdict}
            </span>
          </span>
        ) : null}
      </div>
      {rule.state === "unknown" && !assessedLaterReason ? (
        // No claim from the Hand, so say the plain fact rather than leaving
        // "not assessed" to be read as "ignored".
        <div className="play-meta" data-testid={`not-assessed-${rule.rule_id}`}>
          <span style={{ opacity: 0.8 }}>
            Not assessed: this record did not carry the facts the rule needs.
            It is on the queue for a person.
          </span>
        </div>
      ) : null}
      {rule.state === "unknown" && assessedLaterReason ? (
        // The Hand's own claim about why this rule could not be assessed
        // before drafting. Shown verbatim so a reader can disagree with it.
        <div className="play-meta" data-testid={`assessed-later-${rule.rule_id}`}>
          <span style={{ opacity: 0.8 }}>Not assessed here because: {assessedLaterReason}</span>
        </div>
      ) : null}
      <div className="play-foot">
        <span>{citationWords({ citation: rule.citation || rule.pack_key, origin: rule.origin }, "en")}</span>
        <span>
          pack {rule.pack_version} · as of {rule.as_of}
          {rule.source === "local_fallback" ? " · offline copy" : ""}
        </span>
      </div>
    </div>
  );
}

/** Where a run that threw stopped, in words a person can act on. */
function failedAtLabel(stage: string | undefined): string {
  switch (stage) {
    case "load_inputs":
      return "reading its inputs";
    case "resolve_rules":
      return "fetching the rules";
    case "calculate":
      return "calculating";
    case "pre_gate":
      return "asking GraphIQ";
    case "model":
      return "calling the model";
    case "post_gate":
      return "checking the draft";
    default:
      return stage ?? "before it could decide";
  }
}

function RunDetailInner({ embedded = false }: { embedded?: boolean }) {
  const { workspaceId = "", runId = "" } = useParams();

  const q = useQuery<RunDetailResponse, Error>({
    queryKey: ["hand-run", workspaceId, runId],
    enabled: !!workspaceId && !!runId,
    queryFn: () =>
      apiGet<RunDetailResponse>(
        `/api/workspaces/${workspaceId}/hand-runs/${runId}`,
      ),
  });

  if (q.isLoading) {
    return (
      <div className="main-inner" data-testid="run-detail-loading">
        <div className="lede">Loading…</div>
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="main-inner" data-testid="run-detail-missing">
        <div className="lede">Run not found.</div>
      </div>
    );
  }

  const { run, gate_pre, gate_post, llm, rules, resolution, feedback, checked_against } = q.data;
  const fields = gate_pre.calculated_fields ?? {};
  const failed = gate_post?.failed_validators ?? [];

  return (
    <div className="main-inner" data-testid="run-detail">
      <div className="greet-row">
        <span className="greet">{run.hand_name}</span>
        <span className={`pill ${verdictTone(run.verdict)}`}>
          {verdictLabel(run.verdict)}
        </span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">RUN</div>
        <div className="lede">
          {formatWhen(run.created_at)} ·{" "}
          {(run.duration_ms / 1000).toFixed(1)}s ·{" "}
          {embedded ? null : <Link to={`/workspaces/${workspaceId}/workforce`}>back to roster</Link>}
          {PACK_HANDS.has(run.hand_key) ? (
            <>
              {" · "}
              {/* The pack as the client reads it. A plain link, not a fetch:
                  the browser saves the file and the session cookie goes with
                  the request. */}
              <a
                href={`/api/workspaces/${workspaceId}/hand-runs/${run.run_id}/pack.pptx`}
                data-testid="download-pack"
              >
                download the pack (.pptx)
              </a>
            </>
          ) : null}
        </div>
      </div>

      {gate_pre.failed_at ? (
        <>
          <div className="section-h">
            <span className="ttl">What went wrong</span>
          </div>
          <div className="play-card" data-testid="failure-panel">
            <div className="play-meta">
              <span className="pill red">
                Failed before it could decide — {failedAtLabel(gate_pre.failed_at)}
              </span>
            </div>
            <div style={{ marginTop: 6, lineHeight: 1.5 }}>
              {gate_pre.error ?? "No error text was recorded."}
            </div>
            <div className="meta" style={{ marginTop: 6 }}>
              No model was called after this point. Nothing was sent to anyone.
            </div>
          </div>
        </>
      ) : null}

      {resolution ? (
        <>
          <div className="section-h">
            <span className="ttl">What a person decided</span>
          </div>
          <div className="play-card" data-testid="decision-panel">
            <div className="play-meta">
              <span>
                Decision <span className="v">{resolution.decision.replace(/_/g, " ")}</span>
              </span>
              <span>
                By <span className="v">{roleLabel(resolution.resolved_role)}</span>
              </span>
              <span>
                On{" "}
                <span className="v">{formatWhen(resolution.resolved_at)}</span>
              </span>
            </div>
            <div style={{ marginTop: 6, lineHeight: 1.5 }}>“{resolution.reason}”</div>
          </div>
        </>
      ) : run.verdict === "blocked_pre" || run.verdict === "escalated" ? (
        <div className="play-meta" data-testid="awaiting-decision" style={{ marginTop: 4 }}>
          <span className="pill amber">awaiting a decision — see My queue</span>
        </div>
      ) : null}

      {(checked_against ?? []).length > 0 ? (
        <>
          <div className="section-h">
            <span className="ttl">Checked against</span>
            <span className="meta" data-testid="checked-meta">
              {gate_pre.pack_version ? `pack ${gate_pre.pack_version}` : ""}
              {gate_pre.source === "local_fallback" ? " · offline copy" : " · live from GraphIQ"}
            </span>
          </div>
          <div className="play-grid" data-testid="checked-against">
            {checked_against!.map((p) => (
              <div className="play-card" key={p.pack_key} data-testid={`pack-${p.pack_key}`}>
                <div className="play-head">
                  <div>
                    <div className="play-arch">{p.instrument}</div>
                    <div className="play-title">{p.label}</div>
                  </div>
                  <div className="play-status">
                    <span className={`pill ${p.source === "graphiq" ? "green" : "amber"}`}>
                      {p.source === "graphiq" ? "resolved live" : "offline copy"}
                    </span>
                  </div>
                </div>
                {p.scope ? (
                  <div className="play-meta">
                    <span>{p.scope}</span>
                  </div>
                ) : null}
                <div className="play-meta">
                  <span>
                    Rules applied <span className="v">{p.rules}</span>
                  </span>
                  <span>
                    Obligations <span className="v">{p.obligations}</span>
                  </span>
                  {/* A placeholder may warn and may never block. Saying so
                      here is the difference between a citation and a claim. */}
                  <span>
                    Xamun placeholders <span className="v">{p.placeholders}</span>
                  </span>
                </div>
                {p.articles.length > 0 ? (
                  <div className="play-foot">
                    <span>{p.articles.join(" · ")}</span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      <Verdict
        workspaceId={workspaceId}
        runId={run.run_id}
        feedback={feedback}
      />

      <div className="section-h">
        <span className="ttl">What the code computed</span>
      </div>
      <div className="play-card">
        <CalculatedFields fields={fields} />
      </div>

      <div className="section-h">
        <span className="ttl">Rules in force</span>
      </div>
      <div className="play-grid">
        {rules.length === 0 ? (
          <div className="play-card" data-testid="rules-empty">
            <div className="play-title">No rules were evaluated.</div>
            <div className="play-meta">
              <span>
                That is not the same as no rules applying — see the pre-gate
                verdict above.
              </span>
            </div>
          </div>
        ) : (
          rules.map((r) => (
            <RuleRow
              key={r.rule_id}
              rule={r}
              assessedLaterReason={gate_pre.assessed_by_post_gate?.[r.rule_id]}
            />
          ))
        )}
      </div>

      <div className="section-h">
        <span className="ttl">The model</span>
      </div>
      <div className="play-card" data-testid="model-panel">
        {llm ? (
          <div className="play-meta">
            <span>
              Model <span className="v">{llm.model}</span>
            </span>
            <span>
              Tokens in <span className="v">{llm.tokens_in?.toLocaleString()}</span>
            </span>
            <span>
              Tokens out{" "}
              <span className="v">{llm.tokens_out?.toLocaleString()}</span>
            </span>
            <span>
              Figures substituted{" "}
              <span className="v">{gate_post?.numeric_substitutions ?? 0}</span>
            </span>
          </div>
        ) : (
          <>
            <div className="play-title">No model was called.</div>
            <div className="play-meta">
              <span>
                The deterministic checks stopped this run before any drafting.
              </span>
            </div>
            {(gate_pre.missing ?? []).length > 0 ? (
              <div style={{ marginTop: 8 }} data-testid="missing-list">
                <div className="section-eyebrow">STILL NEEDED</div>
                {(gate_pre.missing ?? []).map((m) => {
                  // A missing entry is often a rule id. Show what the rule
                  // actually asks for — "PRM-007" tells a reviewer nothing,
                  // and they are the person who has to act on it.
                  const rule = rules.find((r) => r.rule_id === m);
                  return (
                    <div key={m} className="play-meta">
                      <span>
                        {rule ? rule.human_summary : m}
                        {rule ? (
                          <span className="v"> ({rule.rule_id})</span>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
      </div>

      {failed.length > 0 ? (
        <>
          <div className="section-h">
            <span className="ttl">Checks that failed</span>
          </div>
          <div className="play-card" data-testid="failed-validators">
            {(gate_post?.outcomes ?? [])
              .filter((o) => !o.ok)
              .map((o) => (
                <div key={o.name} className="play-meta">
                  <span>
                    {validatorLabel(o.name)}{" "}
                    <span className="v">{o.detail ?? ""}</span>
                  </span>
                </div>
              ))}
          </div>
        </>
      ) : null}

      {gate_post?.substituted?.en ? (
        <>
          <div className="section-h">
            <span className="ttl">What it wrote</span>
          </div>
          <div className="play-card" data-testid="narrative-en">
            <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
              {gate_post.substituted.en}
            </div>
          </div>
          {gate_post.substituted.ar ? (
            <div className="play-card" data-testid="narrative-ar">
              <div
                dir="rtl"
                style={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}
              >
                {gate_post.substituted.ar}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** `embedded` renders it inside the everyday app (NEW-UX-7): no console links. */
export function RunDetail({ embedded = false }: { embedded?: boolean } = {}) {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="main-inner" data-testid="run-detail-error">
          <div className="lede">Could not load the run: {error.message}</div>
        </div>
      )}
    >
      <RunDetailInner embedded={embedded} />
    </ErrorBoundary>
  );
}
